import * as RadixToast from '@radix-ui/react-toast';
import { type ComponentType, type ReactNode, type SVGProps, type TransitionEvent, useMemo } from 'react';

import Toast, {
  getSwipeDirection,
  type ToastAction,
  type ToastPlacement,
  type ToastVariant,
} from 'components/ui/shared/Toast/Toast';
import { ToastViewport } from 'components/ui/shared/Toast/Toast.styles';
import useToastStack, { type ToastStackEntry, type ToastStackOrder } from 'components/ui/shared/Toast/useToastStack';
import useToastTimers, { type ToastTimer } from 'components/ui/shared/Toast/useToastTimers';
import { ElementTestId } from 'enums/testing';

const TOAST_STACK_ORDER: ToastStackOrder = 'newest-first';

/**
 * Whether the countdowns are held whenever the reader cannot act on them — pointer over the stack, focus
 * inside it, or the tab out of reach. On, so an undo never expires from under someone still deciding.
 */
const TOAST_HOLDS_WHILE_UNREACHABLE = true;

/**
 * How long a toast stays up before dismissing itself, absent a per-notification override.
 */
const DEFAULT_TOAST_DURATION_MS = 5000;

/**
 * Switches off Radix's countdown, not the auto-dismiss itself — `useToastTimers` runs that instead.
 * `Infinity` is the documented way off: its `startTimer` returns early on it. A per-toast `duration`
 * would revive it, so none is passed to `Toast`.
 */
const RADIX_COUNTDOWN_OFF = Infinity;

export type ActiveNotification = {
  action?: ToastAction;
  children: ReactNode;
  duration?: number;
  /**
   * What raised the toast, if the caller named it. A later notification sharing it closes this one.
   */
  groupKey?: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  /**
   * Monotonic id, keying the toast so each notification is its own mount.
   */
  id: number;
  /**
   * Whether the toast is currently open; false while it plays its exit.
   */
  open: boolean;
  placement: ToastPlacement;
  title?: string;
  variant?: ToastVariant;
};

type Props = {
  /**
   * Whether the tab is hidden or its window unfocused, which holds every countdown in this region.
   */
  isTabUnreachable: boolean;
  /**
   * The toasts sharing this region's placement, oldest first.
   */
  notifications: ActiveNotification[];
  /**
   * Starts a toast's exit.
   */
  onClose: (id: number) => void;
  /**
   * Drops a toast once its exit has finished.
   */
  onRemove: (id: number) => void;
  placement: ToastPlacement;
};

/**
 * Whether a transition ending on a toast means that toast has finished sliding out.
 *
 * Three conditions rather than one: `transitionend` fires per property and bubbles, and every visible
 * card's transform changes on any stack change — so healthy cards emit transform transitions constantly.
 * @param event - the transition that just ended somewhere inside a toast
 */
const isExitTransition = (event: TransitionEvent<HTMLElement>) =>
  event.propertyName === 'transform' &&
  event.target === event.currentTarget &&
  event.currentTarget.dataset.state === 'closed';

/**
 * One edge's toast stack, with its own Radix provider, viewport and countdowns.
 *
 * A region per edge rather than one for the whole app: Radix scopes a single viewport and swipe
 * direction to each provider, which a top-pinned and a bottom-pinned toast cannot share. Expansion and
 * the countdown hold are per region too, since hovering one stack says nothing about the other.
 * @param props - the toasts for this edge, the edge itself, and the lifecycle callbacks
 */
const ToastRegion = ({ isTabUnreachable, notifications, onClose, onRemove, placement }: Props) => {
  const toastEntries = useMemo<ToastStackEntry[]>(
    () => notifications.map(({ id, open }) => ({ id, isOpen: open })),
    [notifications]
  );
  const { cards, isExpanded, isFocused, isHovered, viewportRef } = useToastStack(toastEntries, {
    stackOrder: TOAST_STACK_ORDER,
  });

  /*
   * Closing toasts are left out, or an entry here would start them a fresh countdown mid-exit. A card
   * buried past the visible depth is held instead, so it does not spend its undo before being read.
   */
  const toastTimers = useMemo<ToastTimer[]>(
    () =>
      notifications.flatMap(({ duration, id, open }, index) =>
        open ? { duration: duration ?? DEFAULT_TOAST_DURATION_MS, id, isHeld: !cards[index].isVisible } : []
      ),
    [cards, notifications]
  );

  useToastTimers({
    isPaused: TOAST_HOLDS_WHILE_UNREACHABLE && (isHovered || isFocused || isTabUnreachable),
    onExpire: onClose,
    timers: toastTimers,
  });

  return (
    <RadixToast.Provider
      duration={RADIX_COUNTDOWN_OFF}
      label="Notifications"
      swipeDirection={getSwipeDirection(placement)}
    >
      {notifications.map((notification, index) => {
        const card = cards[index];

        return (
          <Toast
            action={notification.action}
            contentRef={card.contentRef}
            icon={notification.icon}
            isExpanded={isExpanded}
            isFront={card.isFront}
            isVisible={card.isVisible}
            key={notification.id}
            onOpenChange={open => {
              if (!open) {
                onClose(notification.id);
              }
            }}
            onTransitionEnd={event => {
              if (isExitTransition(event)) {
                onRemove(notification.id);
              }
            }}
            open={notification.open}
            placement={placement}
            style={card.style}
            title={notification.title}
            variant={notification.variant}
          >
            {notification.children}
          </Toast>
        );
      })}
      <ToastViewport
        $placement={placement}
        data-placement={placement}
        data-testid={ElementTestId.TOAST_VIEWPORT}
        ref={viewportRef}
      />
    </RadixToast.Provider>
  );
};

export default ToastRegion;
